// Short-time Fourier transform, for drawing a clip as a picture of its pitches.
//
// An amplitude waveform answers "how loud, and when" — enough to make a strip
// tappable, and almost none of what tells one bird from another. A Winter
// Wren's trill and a Varied Thrush's buzz carry the same lumpy envelope; on a
// spectrogram one is a ladder of separate notes and the other a flat band
// sitting on one pitch. Merlin draws these, and so does every field guide
// printed this century, because that is the axis birders actually learn on.
//
// Nothing in here touches the DOM or Web Audio. It takes samples and returns an
// 8-bit image, which keeps it testable under Node and leaves every decision
// about colour to the caller — see src/app/components/spectrogram-image.ts,
// where the ramp is read from the theme's own tokens.

/** A finished spectrogram: one byte of brightness per cell. */
export interface Spectrogram {
  /**
   * `rows × columns` cells, row-major, 0..255. Row 0 is the TOP of the image —
   * the highest frequency — so this can be blitted straight into an ImageData
   * without flipping.
   */
  data: Uint8ClampedArray;
  columns: number;
  rows: number;
  /**
   * Frequency at the top edge, in Hz. The rows below it divide 0..maxHz evenly,
   * and this is what the strip's gridlines are drawn against — it is the bin
   * grid's own ceiling, which lands a few Hz off whatever was requested.
   */
  maxHz: number;
}

export interface SpectrogramOptions {
  /** Time slices to compute. The clip is divided evenly, whatever its length. */
  columns: number;
  /** Frequency rows. Clamped to the bin count — this never invents detail. */
  rows: number;
  /** Ceiling of the displayed band. Clamped to Nyquist. */
  maxHz?: number;
  /** Window length in samples; must be a power of two. */
  fftSize?: number;
}

/**
 * 1024 samples is ~21 ms at 48 kHz (the rate Opus decodes to), and 21 ms is the
 * classic bird-spectrogram compromise: fine enough in frequency to separate the
 * harmonics of a whistle, fast enough in time that the individual notes of a
 * chipping trill stay separate instead of blurring into one band.
 */
const FFT_SIZE = 1024;

/**
 * Birds live under 10 kHz — a Brown Creeper or a Golden-crowned Kinglet, the
 * highest voices in either pack, top out around 8–9 kHz — and the band is fixed
 * rather than fitted per clip on purpose. Comparing two species is the whole
 * exercise, and it only works if the same height means the same pitch on every
 * strip in the app.
 */
const MAX_HZ = 10_000;

/** Silence, as a magnitude. Keeps log(0) out of the picture. */
const FLOOR_MAG = 1e-7;

/**
 * The background level of a row is its 20th percentile over time: in a clip
 * where the bird is quiet more often than it is loud — which is every field
 * recording — that is hiss, wind and traffic. Subtracting it per row is what
 * stops a recording made beside a river from being a solid wash across the
 * bottom of the strip, and it costs one sort per row.
 */
const FLOOR_PERCENTILE = 0.2;

/**
 * How far above its own background a row has to rise before it counts as sound.
 *
 * Six decibels, tuned by eye against four PNW clips rather than derived:
 * chickadee song, a Pacific Wren trill, a hissy Golden-crowned Kinglet call and
 * a robin. At 2 dB the strip speckles — the background's own wander draws a
 * grain over the whole picture that reads as detail. A floor that adapts to each
 * row's spread was tried and dropped: it cleaned nothing the fixed margin did
 * not, and it thinned the wren, whose trill is dense enough to lift the low
 * percentiles it is measured against.
 */
const FLOOR_MARGIN_DB = 6;

/**
 * The image is scaled by ONE span — peak above the typical background — rather
 * than per row. Per-row scaling would give a faint 8 kHz tick the same
 * brightness as the loudest note in the clip, which reads as two birds of equal
 * strength when one of them is barely there.
 */
const MIN_SPAN_DB = 24;

/**
 * ...and no more than this far down, measured from the loudest cell in the
 * clip. The per-row floor is an estimate of the background, and in a studio-
 * quiet recording that estimate sits so low that window leakage 100 dB under
 * the bird counts as "well above background" and draws a haze around every
 * note. An absolute black point is what every spectrogram program has for the
 * same reason.
 *
 * Tightened from 60 to 42 for contrast: the loudest cell and the background are
 * rarely more than that apart in a field recording, and every decibel of range
 * that holds nothing is ramp the bird does not get to use.
 */
const MAX_SPAN_DB = 42;

/**
 * The display curve: a black point, then a gamma that lifts everything above it.
 *
 * One gamma cannot do this job. Below 1 it brightens the marks AND the hiss
 * together and the strip turns grainy; above 1 it cleans the ground by dimming
 * the bird along with it. Every spectrogram program has both knobs for the same
 * reason, and the pair is what separates the two: the knee puts the room the
 * recording was made in at the strip's own background, and the gamma then
 * spends the whole ramp on what is left, which is the bird.
 *
 * Tuned by eye on the four PNW clips this file's other constants were, at four
 * curves each — the alternative to a knee, a plain gamma of 1.2, is legible but
 * noticeably dimmer, which is what "a bit subtle" turned out to mean.
 */
const KNEE = 0.2;
const GAMMA = 0.75;

export function computeSpectrogram(
  samples: Float32Array,
  rate: number,
  opts: SpectrogramOptions,
): Spectrogram {
  const fftSize = opts.fftSize ?? FFT_SIZE;
  const p = plan(fftSize);
  const columns = Math.max(1, Math.floor(opts.columns) || 1);
  const binHz = rate / fftSize;
  const ceiling = Math.min(opts.maxHz ?? MAX_HZ, rate / 2);
  const bins = Math.max(1, Math.min(fftSize / 2, Math.round(ceiling / binHz)));
  // Never more rows than bins: a taller image than the transform measured would
  // be the same fabrication resample() refuses in the waveform. Scaling the
  // finished picture up to the box is the canvas's business, not ours.
  const rows = Math.max(1, Math.min(Math.floor(opts.rows) || 1, bins));

  const window = hann(fftSize);
  // A Hann window's coherent gain is 0.5, so this puts a full-scale sine at
  // exactly 0 dB and everything else below it. The dB constants above are then
  // dBFS, and mean the same thing on every clip.
  const scale = 4 / fftSize;
  const re = new Float32Array(fftSize);
  const im = new Float32Array(fftSize);

  // Frame starts spread evenly across the clip, the first window flush with the
  // start and the last flush with the end. Overlap falls out of the clip's
  // length rather than being a setting: a 2 s clip gets deep overlap and a 13 s
  // clip about half, and both fill the same strip.
  const span = Math.max(0, samples.length - fftSize);
  const step = columns > 1 ? span / (columns - 1) : 0;

  const cells = new Float32Array(rows * columns).fill(-Infinity);

  for (let c = 0; c < columns; c++) {
    const start = Math.round(c * step);
    for (let j = 0; j < fftSize; j++) {
      const s = start + j;
      re[j] = s < samples.length ? samples[s] * window[j] : 0;
      im[j] = 0;
    }
    fft(re, im, p);
    for (let b = 0; b < bins; b++) {
      const mag = Math.hypot(re[b], im[b]) * scale;
      const db = 20 * Math.log10(Math.max(mag, FLOOR_MAG));
      // Bins fold into rows by MAXIMUM, not by mean. A single-bin whistle
      // averaged with its silent neighbours fades out at exactly the strip
      // heights where it matters most; taking the loudest keeps a thin line
      // thin and visible, which is how it sounds.
      const row = rows - 1 - Math.min(rows - 1, Math.floor((b * rows) / bins));
      const at = row * columns + c;
      if (db > cells[at]) cells[at] = db;
    }
  }

  return {
    data: normalize(cells, rows, columns),
    columns,
    rows,
    maxHz: bins * binHz,
  };
}

/** dB cells → 0..255, floored per row and scaled by one global span. */
function normalize(
  cells: Float32Array,
  rows: number,
  columns: number,
): Uint8ClampedArray {
  const floors = new Float32Array(rows);
  const scratch = new Float32Array(columns);
  let peak = -Infinity;
  for (let r = 0; r < rows; r++) {
    scratch.set(cells.subarray(r * columns, (r + 1) * columns));
    scratch.sort();
    floors[r] = scratch[Math.floor(FLOOR_PERCENTILE * (columns - 1))];
    if (scratch[columns - 1] > peak) peak = scratch[columns - 1];
  }

  const sorted = Float32Array.from(floors).sort();
  const typical = sorted[sorted.length >> 1];
  // The lower guard is what keeps a near-silent clip dark: without it, dividing
  // by a span of half a decibel stretches pure hiss across the whole ramp and
  // draws a snowstorm that looks exactly like a bird.
  const span = Math.min(Math.max(peak - typical, MIN_SPAN_DB), MAX_SPAN_DB);
  const black = peak - MAX_SPAN_DB;

  const out = new Uint8ClampedArray(rows * columns);
  for (let r = 0; r < rows; r++) {
    const floor = Math.max(floors[r] + FLOOR_MARGIN_DB, black);
    for (let c = 0; c < columns; c++) {
      const at = r * columns + c;
      const t = (cells[at] - floor) / span;
      out[at] = t <= KNEE ? 0 : 255 * Math.pow((Math.min(1, t) - KNEE) / (1 - KNEE), GAMMA);
    }
  }
  return out;
}

const windows = new Map<number, Float32Array>();

/** Hann. Cached: every strip in the app asks for the same one. */
function hann(n: number): Float32Array {
  const hit = windows.get(n);
  if (hit) return hit;
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / n);
  windows.set(n, w);
  return w;
}

// ---- Radix-2 FFT -----------------------------------------------------------
//
// Iterative, in place, with the twiddle factors and the bit-reversal
// permutation precomputed once per size. A library would be a dependency and a
// download for forty lines, and the app already declines heavier ones for less.

interface Plan {
  n: number;
  levels: number;
  cos: Float32Array;
  sin: Float32Array;
  rev: Uint32Array;
}

const plans = new Map<number, Plan>();

function plan(n: number): Plan {
  const hit = plans.get(n);
  if (hit) return hit;
  const levels = Math.log2(n);
  if (n < 2 || !Number.isInteger(levels)) {
    throw new Error(`fftSize must be a power of two, got ${n}`);
  }
  const cos = new Float32Array(n / 2);
  const sin = new Float32Array(n / 2);
  for (let i = 0; i < n / 2; i++) {
    cos[i] = Math.cos((2 * Math.PI * i) / n);
    sin[i] = Math.sin((2 * Math.PI * i) / n);
  }
  const rev = new Uint32Array(n);
  for (let i = 0; i < n; i++) {
    let x = i;
    let r = 0;
    for (let j = 0; j < levels; j++) {
      r = (r << 1) | (x & 1);
      x >>= 1;
    }
    rev[i] = r;
  }
  const made: Plan = { n, levels, cos, sin, rev };
  plans.set(n, made);
  return made;
}

/** Forward transform of `re`/`im`, in place. */
function fft(re: Float32Array, im: Float32Array, p: Plan): void {
  const n = p.n;
  for (let i = 0; i < n; i++) {
    const j = p.rev[i];
    if (j > i) {
      let t = re[i];
      re[i] = re[j];
      re[j] = t;
      t = im[i];
      im[i] = im[j];
      im[j] = t;
    }
  }
  for (let size = 2; size <= n; size *= 2) {
    const half = size / 2;
    const stride = n / size;
    for (let i = 0; i < n; i += size) {
      for (let j = i, k = 0; j < i + half; j++, k += stride) {
        const l = j + half;
        const tre = re[l] * p.cos[k] + im[l] * p.sin[k];
        const tim = -re[l] * p.sin[k] + im[l] * p.cos[k];
        re[l] = re[j] - tre;
        im[l] = im[j] - tim;
        re[j] += tre;
        im[j] += tim;
      }
    }
  }
}
